import {
  CONNECTION_LABEL,
  DEFAULT_RAG_LABEL,
  DEFAULT_RAG_STATUS,
  RAG_BADGE_CLASS,
  RAG_LABEL,
  RAG_LABEL_BY_STATUS,
  RAG_STATUS,
  TABLE_STATUS_CLASS_SUFFIX,
} from '../constants/deviceRag';

function resolveRagStatus(device) {
  const raw = device?.ragStatus;
  if (raw && RAG_BADGE_CLASS[raw]) {
    return raw;
  }
  const displayStatus = device?.status;
  if (displayStatus === RAG_LABEL.ACTIVE) {
    return RAG_STATUS.ACTIVE;
  }
  if (displayStatus === RAG_LABEL.STANDBY) {
    return RAG_STATUS.STANDBY;
  }
  if (displayStatus === RAG_LABEL.OFFLINE) {
    return RAG_STATUS.OFFLINE;
  }
  return DEFAULT_RAG_STATUS;
}

const normalizeDeviceId = (device) => {
  if (!device) {
    return null;
  }
  return String(device.deviceId || device._id || '');
};

function parseSeenAtMs(value) {
  if (value == null) {
    return null;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Merge cached RAG + battery fields from list/location/socket rows onto a device detail record. */
export function mergeDeviceRagFields(primary, ...sources) {
  if (!primary) {
    return primary;
  }
  const merged = { ...primary };
  let bestSeenAtMs = parseSeenAtMs(merged.lastSeenAt);

  sources.forEach((source) => {
    if (!source) {
      return;
    }
    const sourceSeenAtMs = parseSeenAtMs(source.lastSeenAt);
    const sourceIsNewer = sourceSeenAtMs != null
      && (bestSeenAtMs == null || sourceSeenAtMs >= bestSeenAtMs);

    if (sourceIsNewer) {
      bestSeenAtMs = sourceSeenAtMs;
      if (source.ragStatus != null) {
        merged.ragStatus = source.ragStatus;
      }
      if (source.status != null) {
        merged.status = source.status;
      }
      merged.lastSeenAt = source.lastSeenAt;
      if (source.lastEventType != null) {
        merged.lastEventType = source.lastEventType;
      }
      if (source.batteryLevel != null) {
        merged.batteryLevel = source.batteryLevel;
      }
      if (source.lock_status != null) {
        merged.lock_status = source.lock_status;
      }
      return;
    }

    if (merged.ragStatus == null && source.ragStatus != null) {
      merged.ragStatus = source.ragStatus;
    }
    if (merged.status == null && source.status != null) {
      merged.status = source.status;
    }
    if (merged.lastSeenAt == null && source.lastSeenAt != null) {
      merged.lastSeenAt = source.lastSeenAt;
    }
    if (merged.lastEventType == null && source.lastEventType != null) {
      merged.lastEventType = source.lastEventType;
    }
    if (merged.lock_status == null && source.lock_status != null) {
      merged.lock_status = source.lock_status;
    }
    if (merged.batteryLevel == null && source.batteryLevel != null) {
      merged.batteryLevel = source.batteryLevel;
    }
  });
  return merged;
}

export function findDeviceInList(device, list) {
  if (!device || !Array.isArray(list) || list.length === 0) {
    return null;
  }
  const deviceId = normalizeDeviceId(device);
  if (!deviceId) {
    return null;
  }
  return list.find((row) => normalizeDeviceId(row) === deviceId) || null;
}

/**
 * Map API device row → display props for DeviceStatusBadge / tables.
 * No heartbeat math — backend + cron own thresholds.
 * Label + color both derive from ragStatus so map badges never show "Offline" on green.
 */
export function getDeviceRagView(device) {
  const ragStatus = resolveRagStatus(device);
  const label = RAG_LABEL_BY_STATUS[ragStatus] ?? DEFAULT_RAG_LABEL;

  return {
    ragStatus,
    label,
    isConnected: ragStatus !== RAG_STATUS.OFFLINE,
    connectionText:
      ragStatus === RAG_STATUS.OFFLINE
        ? CONNECTION_LABEL.NOT_CONNECTED
        : CONNECTION_LABEL.CONNECTED,
    badgeClass: RAG_BADGE_CLASS[ragStatus] ?? RAG_BADGE_CLASS[RAG_STATUS.OFFLINE],
    tableClass: `${label.toLowerCase()}${TABLE_STATUS_CLASS_SUFFIX}`,
    lastSeenAt: device?.lastSeenAt ?? null,
  };
}

export { RAG_STATUS, RAG_LABEL, CONNECTION_LABEL };
